import { createMenuAim } from '@theme/menu-aim';

export const HEADER_NAV_TREE_MQ = '(min-width: 990px)';
const DESKTOP_MQ = HEADER_NAV_TREE_MQ;
const HOVER_MQ = '(hover: hover) and (pointer: fine)';

/**
 * Desktop mega-menu only. Touch, coarse pointer, or viewports under 990px
 * must not mutate nested L2/L3 panels — the drawer owns that tree.
 * @returns {boolean}
 */
export function isDesktopMegaHover() {
  return window.matchMedia(DESKTOP_MQ).matches && window.matchMedia(HOVER_MQ).matches;
}

/**
 * One DOM node: nested L1/L2/L3 lives in header-menu on desktop and in the
 * drawer host on small viewports. Links are never cloned.
 */
export function placeHeaderNavTree() {
  const tree = document.querySelector('[data-header-nav-tree]');
  if (!(tree instanceof HTMLElement)) return tree;

  const home = document.querySelector('[data-header-nav-tree-home]');
  const host = document.querySelector('[data-header-nav-tree-host]');
  const desktop = window.matchMedia(HEADER_NAV_TREE_MQ).matches;

  if (desktop && home instanceof HTMLElement && tree.parentElement !== home) {
    home.appendChild(tree);
  } else if (!desktop && host instanceof HTMLElement && tree.parentElement !== host) {
    host.appendChild(tree);
  }

  return tree;
}

/**
 * @param {HTMLElement} link
 * @returns {boolean}
 */
function isLinkKeyboardFocused(link) {
  if (!(link instanceof HTMLElement)) return false;

  try {
    if (CSS.supports('selector(:focus-visible)')) {
      return link.matches(':focus-visible');
    }
  } catch {
    // ignore
  }

  return link.matches(':focus');
}

/**
 * @param {HTMLElement} panel
 * @returns {HTMLElement | null}
 */
function panelHome(panel) {
  if (panel._treeHome instanceof HTMLElement) return panel._treeHome;
  const home = panel.parentElement;
  if (home instanceof HTMLElement) panel._treeHome = home;
  return home;
}

/**
 * @param {HTMLElement} panel
 */
function restorePanel(panel) {
  const home = panelHome(panel);
  if (home && panel.parentElement !== home) home.appendChild(panel);
  panel.hidden = true;
  panel.style.display = 'none';
}

/**
 * @param {HTMLElement} container
 */
function initMegaMenuThreeColumn(container) {
  if (!container || container.dataset.megaMenuThreeColumnInit === 'true') return;
  container.dataset.megaMenuThreeColumnInit = 'true';

  try {
    placeHeaderNavTree();

    const mqDesktop = window.matchMedia(DESKTOP_MQ);
    const mqHover = window.matchMedia(HOVER_MQ);

    const level2Column = container.querySelector('.mega-menu__column--level-2');
    const level2ColumnContent = level2Column?.querySelector('.mega-menu__column-content');
    const level2ColumnHeader = level2Column?.querySelector('.mega-menu__column-header');
    const level3Column = container.querySelector('.mega-menu__column--level-3');
    const level3ColumnContent = level3Column?.querySelector('.mega-menu__column-content');
    const level3ColumnHeader = level3Column?.querySelector('.mega-menu__column-header');

    const tree = () => document.querySelector('[data-header-nav-tree]');
    const level1Links = () => tree()?.querySelectorAll('a.menu-level-1-link') ?? [];

    if (!tree()?.querySelector('a.menu-level-1-link') || !level2Column || !level3Column || !level2ColumnContent || !level3ColumnContent) {
      return;
    }

    const menuAim = createMenuAim(
      (targetEl) => {
        if (!targetEl) return null;
        const isLevel3 = targetEl.dataset?.aimTarget === 'level3';
        if (isLevel3) {
          const visible = getComputedStyle(level3Column).display !== 'none';
          if (visible) return level3Column.getBoundingClientRect();
          const l2 = level2Column.getBoundingClientRect();
          const width = Math.max(240, l2.width || 320);
          return new DOMRect(l2.right, l2.top, width, Math.max(1, l2.bottom - l2.top));
        }
        const l2Visible = getComputedStyle(level2Column).display !== 'none';
        if (l2Visible) return level2Column.getBoundingClientRect();
        const linkRect = targetEl.getBoundingClientRect();
        const cont = container.getBoundingClientRect();
        const width = Math.max(240, (cont.width * 0.25) | 0);
        return new DOMRect(linkRect.right, cont.top, width, Math.max(1, cont.bottom - cont.top));
      },
      { openDelayTowards: 300, closeDelay: 250 }
    );

    let activationTimer = null;
    let activationTimerId = 0;
    let closeTimer = null;

    function startTrackingIfDesktop() {
      if (mqDesktop.matches && mqHover.matches) menuAim.start();
    }
    function stopTracking() {
      menuAim.stop();
    }

    function childList(link) {
      const next = link?.nextElementSibling;
      return next instanceof HTMLUListElement ? next : null;
    }

    function hideAllL2Panels() {
      container.querySelectorAll('.mega-menu__level2-panel').forEach((p) => {
        if (p instanceof HTMLElement) restorePanel(p);
      });
    }

    function hideAllL3Panels() {
      container.querySelectorAll('.mega-menu__level3-panel').forEach((p) => {
        if (p instanceof HTMLElement) restorePanel(p);
      });
    }

    function linkTitle(link) {
      return link ? link.textContent.trim() : '';
    }

    function setL2HeaderFromL1(l1Link) {
      if (level2ColumnHeader) level2ColumnHeader.textContent = linkTitle(l1Link);
    }

    function setL3HeaderFromL2(l2Link) {
      if (level3ColumnHeader) level3ColumnHeader.textContent = linkTitle(l2Link);
    }

    function columnsActive() {
      return isDesktopMegaHover() && Boolean(container.contains(tree()));
    }

    function showL2Panel(l1Link) {
      if (!columnsActive()) return;
      hideAllL2Panels();
      hideAllL3Panels();
      level3Column.style.display = 'none';

      const panel = childList(l1Link);
      const hasLinks = panel instanceof HTMLElement && panel.querySelector('a.menu-level-2-link');
      if (!hasLinks) {
        level2Column.style.display = 'none';
        return;
      }
      panelHome(panel);
      if (columnsActive()) {
        level2ColumnContent.appendChild(panel);
      }
      panel.hidden = false;
      panel.style.display = 'flex';
      panel.style.flexDirection = 'column';
      level2Column.style.display = columnsActive() ? 'flex' : 'none';
    }

    function showL3Panel(l2Link) {
      if (!columnsActive()) return;
      hideAllL3Panels();
      const panel = childList(l2Link);
      if (!(panel instanceof HTMLElement) || !panel.querySelector('a.mega-menu__link:not(.mega-menu__view-all)')) {
        level3Column.style.display = 'none';
        return;
      }
      panelHome(panel);
      if (columnsActive()) {
        level3ColumnContent.appendChild(panel);
      }
      panel.hidden = false;
      panel.style.display = 'flex';
      panel.style.flexDirection = 'column';
      level3Column.style.display = columnsActive() ? 'flex' : 'none';
    }

    function clearL2Active() {
      container.querySelectorAll('.menu-level-2-link').forEach((l) => {
        l.classList.remove('active');
        l.removeAttribute('aria-expanded');
      });
    }

    function activateLevel1(l1Link) {
      level1Links().forEach((l) => {
        l.classList.remove('active');
        l.removeAttribute('aria-expanded');
      });
      clearL2Active();
      hideAllL3Panels();
      level3Column.style.display = 'none';
      setL2HeaderFromL1(l1Link);
      showL2Panel(l1Link);
      l1Link.setAttribute('aria-expanded', 'true');
      l1Link.classList.add('active');
    }

    function wireLevel2Links() {
      container.querySelectorAll('.menu-level-2-link[data-has-children="true"]').forEach((l2) => {
          l2.addEventListener('mouseenter', function () {
            if (!isDesktopMegaHover()) return;
            if (!childList(l2)) return;

            l2.dataset.aimTarget = 'level3';
            const currentTimerId = ++activationTimerId;
            if (activationTimer) clearTimeout(activationTimer);

            const delay = menuAim.delayFor(l2);
            activationTimer = setTimeout(() => {
              if (currentTimerId !== activationTimerId) return;
              setL3HeaderFromL2(l2);
              showL3Panel(l2);
              clearL2Active();
              l2.classList.add('active');
              l2.setAttribute('aria-expanded', 'true');
            }, delay);
          });

          l2.addEventListener('focus', function () {
            if (!isDesktopMegaHover()) return;
            if (!isLinkKeyboardFocused(l2)) return;
            if (!childList(l2)) return;
            clearTimeout(activationTimer);
            setL3HeaderFromL2(l2);
            showL3Panel(l2);
            clearL2Active();
            l2.classList.add('active');
            l2.setAttribute('aria-expanded', 'true');
          });
        });
    }

    wireLevel2Links();

    level3Column.addEventListener('mouseenter', () => {
      if (activationTimer) {
        clearTimeout(activationTimer);
        activationTimer = null;
      }
    });

    level1Links().forEach((link) => {
      const hasSubmenu = link.dataset.hasChildren === 'true';

      link.addEventListener('mouseenter', function () {
        if (!isDesktopMegaHover()) return;
        if (!hasSubmenu) return;

        clearTimeout(activationTimer);
        link.dataset.aimTarget = 'level2';
        const delay = menuAim.delayFor(link);
        activationTimer = setTimeout(() => {
          activateLevel1(link);
        }, delay);
      });

      link.addEventListener('focus', function () {
        if (!isDesktopMegaHover()) return;
        if (!hasSubmenu) return;
        if (!isLinkKeyboardFocused(link)) return;

        clearTimeout(activationTimer);
        activateLevel1(link);
      });
    });

    container.addEventListener('mouseenter', startTrackingIfDesktop);
    container.addEventListener('mouseleave', function () {
      clearTimeout(activationTimer);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        level2Column.style.display = 'none';
        level3Column.style.display = 'none';
        hideAllL2Panels();
        hideAllL3Panels();
        level1Links().forEach((l) => {
          l.removeAttribute('aria-expanded');
          l.classList.remove('active');
        });
        clearL2Active();
        stopTracking();
      }, 250);
    });

    function collapseDesktopColumns() {
      stopTracking();
      clearTimeout(activationTimer);
      clearTimeout(closeTimer);
      level2Column.style.display = 'none';
      level3Column.style.display = 'none';
      hideAllL2Panels();
      hideAllL3Panels();
      level1Links().forEach((l) => {
        l.removeAttribute('aria-expanded');
        l.classList.remove('active');
      });
      clearL2Active();
    }

    mqDesktop.addEventListener?.('change', () => {
      if (!isDesktopMegaHover()) collapseDesktopColumns();
    });
    mqHover.addEventListener?.('change', () => {
      if (!isDesktopMegaHover()) collapseDesktopColumns();
    });
  } catch (e) {
    console.error('Mega menu three-column error:', e);
  }
}

export { initMegaMenuThreeColumn };

function initAllMegaMenuThreeColumns() {
  placeHeaderNavTree();
  document.querySelectorAll('[data-mega-menu-three-column]').forEach(initMegaMenuThreeColumn);
}

initAllMegaMenuThreeColumns();
window.matchMedia(HEADER_NAV_TREE_MQ).addEventListener?.('change', () => {
  placeHeaderNavTree();
});
